/* =============================================
   Upsell Carousel
   - Cyclic infinite scroll
   - Drag / touch support
   - Add to cart + Dawn drawer refresh
   ============================================= */

class UpsellCarousel {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('.upsell-carousel__track');
    this.origItems = Array.from(this.track.querySelectorAll('.upsell-card'));
    this.count = this.origItems.length;
    this.i18n = {
      add: el.dataset.i18nAdd || 'Add',
      added: el.dataset.i18nAdded || 'Added',
    };

    if (this.count === 0) return;

    // Drag state — initialized for all sizes so drag always works
    this.isDragging = false;
    this._swipeDir = null;
    this.startX = 0;
    this.startY = 0;
    this.startTranslate = 0;
    this.currentTranslate = 0;
    this.currentIndex = 0;

    this._trackImpressions();

    // 1 item — full width, no carousel
    if (this.count === 1) {
      el.classList.add('upsell-carousel--single');
      this._bindEvents();
      return;
    }

    // 2 items — show both, draggable to reveal second card
    if (this.count === 2) {
      el.classList.add('upsell-carousel--static');
      this._bindEvents();
      return;
    }

    // 3+ items — reveal nav arrows + infinite scroll
    this.nav = el.closest('.upsell-section')?.querySelector('.upsell-carousel__nav');
    if (this.nav) {
      this.nav.classList.add('is-visible');
      this.nav.removeAttribute('aria-hidden');
    }

    this._buildClones();
    this._bindEvents();
  }

  /* ---- Build infinite clones ---- */
  _buildClones() {
    // Append clones of all items at end + start (for wraparound)
    const clone = (item) => {
      const c = item.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      return c;
    };

    // Append copies at end
    this.origItems.forEach(item => this.track.appendChild(clone(item)));
    // Prepend copies at start
    [...this.origItems].reverse().forEach(item => this.track.prepend(clone(item)));

    this.allItems = Array.from(this.track.querySelectorAll('.upsell-card'));
    this.cloneOffset = this.count; // number of prepended clones
    this._goTo(this.cloneOffset, false);
  }

  _itemWidth() {
    const item = this.allItems[0];
    if (!item) return 0;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 8;
    return item.offsetWidth + gap;
  }

  _goTo(index, animate = true) {
    this.currentIndex = index;
    const tx = -index * this._itemWidth();
    this.currentTranslate = tx;
    this.track.style.transition = animate
      ? 'transform 0.32s cubic-bezier(0.25,0.46,0.45,0.94)'
      : 'none';
    this.track.style.transform = `translateX(${tx}px)`;
  }

  _next() {
    this._goTo(this.currentIndex + 1);
    this._checkBounds();
  }

  _prev() {
    this._goTo(this.currentIndex - 1);
    this._checkBounds();
  }

  _checkBounds() {
    const onEnd = () => {
      // Past the end clones → jump to real start
      if (this.currentIndex >= this.cloneOffset + this.count) {
        this._goTo(this.cloneOffset, false);
      }
      // Before the start clones → jump to real end
      if (this.currentIndex < this.cloneOffset) {
        this._goTo(this.cloneOffset + this.count - 1, false);
      }
    };
    this.track.addEventListener('transitionend', onEnd, { once: true });
  }

  /* ---- Drag / touch ---- */
  _getX(e) {
    return e.touches ? e.touches[0].clientX : e.clientX;
  }

  _onDragStart(e) {
    if (e.target.closest('.upsell-card__add') || e.target.closest('a')) return;
    this.isDragging = true;
    this._swipeDir = null;
    this.startX = this._getX(e);
    this.startY = e.touches ? e.touches[0].clientY : e.clientY;
    this.startTranslate = this.currentTranslate;
    this.track.classList.add('is-dragging');
    this.track.style.transition = 'none';
  }

  _onDragMove(e) {
    if (!this.isDragging) return;
    const diffX = this._getX(e) - this.startX;
    const diffY = (e.touches ? e.touches[0].clientY : e.clientY) - this.startY;

    // Determine swipe direction on first significant move
    if (!this._swipeDir && (Math.abs(diffX) > 4 || Math.abs(diffY) > 4)) {
      this._swipeDir = Math.abs(diffX) >= Math.abs(diffY) ? 'h' : 'v';
    }

    // Vertical swipe → let the drawer scroll, cancel carousel drag
    if (this._swipeDir === 'v') {
      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      if (this.allItems) {
        this._goTo(this.currentIndex);
      } else {
        this.track.style.transition = 'transform 0.28s ease';
        this.track.style.transform = `translateX(${this.currentTranslate}px)`;
      }
      return;
    }

    // Horizontal swipe → block scroll, move carousel
    if (this._swipeDir === 'h' && e.cancelable) e.preventDefault();

    const tx = this.startTranslate + diffX;
    this.currentTranslate = tx;
    this.track.style.transform = `translateX(${tx}px)`;
  }

  _onDragEnd(e) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.classList.remove('is-dragging');
    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const diff = endX - this.startX;
    if (this.allItems) {
      // Full infinite carousel — snap to next/prev or current
      if (Math.abs(diff) > 40) {
        diff < 0 ? this._next() : this._prev();
      } else {
        this._goTo(this.currentIndex);
      }
    } else {
      // Static/single mode — clamp translate to valid range
      this.track.style.transition = 'transform 0.28s ease';
      const min = -(this.track.scrollWidth - this.el.offsetWidth);
      const clamped = Math.max(min, Math.min(0, this.currentTranslate));
      this.currentTranslate = clamped;
      this.track.style.transform = `translateX(${clamped}px)`;
    }
  }

  /* ---- Impressions ---- */
  _trackImpressions() {
    const items = this.origItems.length ? this.origItems : Array.from(this.track.querySelectorAll('.upsell-card'));
    items.forEach(card => {
      const btn = card.querySelector('.upsell-card__add');
      if (btn) this._track('upsell_impression', this._eventData(btn));
    });
  }

  /* ---- Analytics ---- */
  _track(eventName, data = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...data });
  }

  _eventData(btn) {
    return {
      variant_id: btn.dataset.variantId,
      product_title: btn.dataset.productTitle || '',
      price: btn.dataset.price ? Number(btn.dataset.price) / 100 : undefined,
    };
  }

  /* ---- Add to cart ---- */
  async _addToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.classList.contains('is-loading')) return;

    btn.classList.add('is-loading');
    this._track('upsell_add_click', this._eventData(btn));

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });

      if (!res.ok) throw new Error('Add failed');

      this._track('upsell_add_success', this._eventData(btn));

      // Success feedback on all copies of this button (clones)
      this.track.querySelectorAll(`[data-variant-id="${variantId}"]`).forEach(b => {
        b.classList.remove('is-loading');
        b.classList.add('is-added');
        b.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg><span>${this.i18n.added}</span>`;
      });

      // Refresh Dawn cart drawer
      this._refreshDrawer();

    } catch (err) {
      btn.classList.remove('is-loading');
      this._track('upsell_add_error', this._eventData(btn));
      console.error('[Upsell] Add to cart error:', err);
    }
  }

  _refreshDrawer() {
    const cartUrl = window.routes?.cart_url || '/cart';
    fetch(`${cartUrl}?section_id=cart-drawer`)
      .then(r => r.text())
      .then(html => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        ['cart-drawer-items', '.cart-drawer__footer', '.free-shipping-bar'].forEach(sel => {
          const target = document.querySelector(sel);
          const source = parsed.querySelector(sel);
          if (target && source) target.replaceWith(source);
        });
        // Re-init carousels after DOM replacement
        initUpsellCarousels();
      })
      .catch(console.error);
  }

  /* ---- Events ---- */
  _bindEvents() {
    // Drag — mouse
    this.track.addEventListener('mousedown', this._onDragStart.bind(this));
    window.addEventListener('mousemove', this._onDragMove.bind(this));
    window.addEventListener('mouseup', this._onDragEnd.bind(this));

    // Drag — touch (non-passive so we can preventDefault on horizontal swipes)
    this.track.addEventListener('touchstart', this._onDragStart.bind(this), { passive: true });
    this.track.addEventListener('touchmove', this._onDragMove.bind(this), { passive: false });
    this.track.addEventListener('touchend', this._onDragEnd.bind(this));

    // Add to cart (delegated — works on clones too)
    this.track.addEventListener('click', e => {
      const btn = e.target.closest('.upsell-card__add');
      if (btn) this._addToCart(btn);
    });

    // Nav arrows (only present for 3+ items)
    if (this.nav) {
      this.nav.querySelector('.upsell-carousel__arrow--prev')
        ?.addEventListener('click', () => this._prev());
      this.nav.querySelector('.upsell-carousel__arrow--next')
        ?.addEventListener('click', () => this._next());
    }
  }
}

/* ---- Init / re-init ---- */
function initUpsellCarousels() {
  document.querySelectorAll('[data-upsell-carousel]').forEach(el => {
    if (el.dataset.upsellReady) return;
    el.dataset.upsellReady = '1';
    new UpsellCarousel(el);
  });
}

document.addEventListener('DOMContentLoaded', initUpsellCarousels);
document.addEventListener('cart-drawer:open', initUpsellCarousels);
